/**
 * Editable model for the live plan card (AI reco 3A).
 *
 * The card is derived from `messages[].plan.plan` in the agent store and is
 * rebuilt wholesale whenever the server plan changes (`buildHydratedPlanMessage`,
 * a second `set_plan` mints a fresh message id → full remount). Edits therefore
 * cannot live in component state; they live in the store, keyed by planId, as a
 * `PlanEditDraft` built by this module.
 *
 * Two invariants this model exists to protect:
 *
 *  1. **Rich fields survive.** `LivePlanJson` is a partial view — the persisted
 *     `plan_json` also carries `proofPoints`, `soWhat`, `evidenceMap`,
 *     `orgInclusions`, `evaluatorFocus`, `differentiationAngle`, `openQuestions`,
 *     `riskFlags`, `sectionType`, `draftingDirectives` and more. The draft keeps
 *     the whole base object and rebuilds by SPREADING it, never by constructing
 *     a literal.
 *  2. **Key points are edited by identity, not by rendered position.** The card
 *     hides legacy machinery lines (`PLAN_MACHINERY_KEYPOINT_PATTERN`), so the
 *     i-th rendered bullet is NOT the i-th stored string. Every key point gets a
 *     stable synthetic id at draft time; mutators address that id.
 */

import type { LivePlanJson, LivePlanSection } from '@/lib/api'

// ===================
// Machinery key points
// ===================

/**
 * Pre-2026-07-22 `plan_json` carried a server-injected drafter directive inside
 * `keyPoints[0]` ("Use these as ### (h3) in-body headings … Do NOT insert
 * horizontal rules (---) between headings."). The backend now filters it at the
 * `set_plan` boundary (twin:
 * `backend/src/lib/orchestrator/plan-tools.ts PLAN_MACHINERY_KEYPOINT_PATTERN`);
 * this is the render-side belt for already-persisted polluted plans.
 * Frontend can't import backend — keep this regex in sync with its twin.
 */
export const PLAN_MACHINERY_KEYPOINT_PATTERN =
  /### \(h3\)|#### \(h4\)|in-body headings|horizontal rules \(---\)/i

export function isPlanMachineryKeyPoint(point: string): boolean {
  return PLAN_MACHINERY_KEYPOINT_PATTERN.test(point)
}

// ===================
// Draft model
// ===================

export interface PlanKeyPointDraft {
  /**
   * Stable for the life of the draft. Survives insertion, removal and reorder
   * of siblings — this is what mutators address, never the array index and
   * never the index in the machinery-filtered render list.
   */
  id: string
  text: string
  /**
   * A legacy drafter directive. Hidden by the card (`visibleKeyPoints`) but
   * carried through a save so an edit never silently deletes stored content.
   */
  isMachinery: boolean
}

export interface PlanSectionDraft {
  sectionId: string
  heading: string
  targetWordCount: number
  keyPoints: PlanKeyPointDraft[]
  /**
   * Present only when the stored section had it. Omitting the key on save keeps
   * the stored value; sending `[]` clears it — so an absent value must stay
   * absent all the way through `planDraftToLivePlan`.
   */
  evidenceHints?: string[]
}

export interface PlanEditDraft {
  planId: string
  /** The plan `version` this draft was forked from — sent as `expectedVersion`. */
  baseVersion: number
  /**
   * The full stored plan the draft was forked from, rich fields included. Saves
   * spread this; nothing else may reconstruct it.
   */
  base: LivePlanJson
  goal: string
  sections: PlanSectionDraft[]
  isDirty: boolean
  isSaving: boolean
  /** Section ids the last save attempt flagged. Cleared by the next edit. */
  conflictingSectionIds: string[]
  /** Monotonic allocator for synthetic key-point ids. */
  nextKeyPointSeq: number
}

// ===================
// Construction
// ===================

/**
 * Allocate the draft view of a plan's sections, key-point ids included.
 *
 * Exported because the CARD needs these ids before any edit has happened: the
 * store forks its draft lazily (the first mutator calls `beginPlanEdit`), so a
 * card with no draft yet still has to name the key point the writer just typed
 * into. Both sides going through this one allocator is what guarantees the id
 * the card sends matches the id the store is about to mint — recomputing the
 * `kp-N` scheme anywhere else would drift the moment the scheme changes.
 *
 * The counter runs across ALL sections, in stored order. That is deliberate:
 * ids must be unique within the draft, not within a section, because a key
 * point can outlive the reordering and removal of its siblings.
 */
export function planSectionDrafts(plan: LivePlanJson): {
  sections: PlanSectionDraft[]
  nextKeyPointSeq: number
} {
  let seq = 0
  const sections: PlanSectionDraft[] = (plan.sections ?? []).map((section) => ({
    sectionId: section.sectionId,
    heading: section.heading,
    targetWordCount: section.targetWordCount,
    keyPoints: (section.keyPoints ?? []).map((text) => ({
      id: `kp-${seq++}`,
      text,
      isMachinery: isPlanMachineryKeyPoint(text),
    })),
    ...(section.evidenceHints ? { evidenceHints: [...section.evidenceHints] } : {}),
  }))
  return { sections, nextKeyPointSeq: seq }
}

/**
 * Can a card at this version be edited at all?
 *
 * `expectedVersion` is `z.number().int().positive()` server-side, so a card
 * hydrated without a usable version (both hydration paths fall back to 0 when
 * the server omits one) can never commit an edit. Gate the card's edit
 * affordances on this so the writer gets a read-only card rather than an editor
 * that silently swallows every keystroke.
 */
export function isEditablePlanVersion(version: unknown): version is number {
  return typeof version === 'number' && Number.isInteger(version) && version > 0
}

export function createPlanEditDraft(
  planId: string,
  plan: LivePlanJson,
  baseVersion: number
): PlanEditDraft {
  const { sections, nextKeyPointSeq: seq } = planSectionDrafts(plan)

  return {
    planId,
    baseVersion,
    base: plan,
    goal: plan.goal,
    sections,
    isDirty: false,
    isSaving: false,
    conflictingSectionIds: [],
    nextKeyPointSeq: seq,
  }
}

/** The key points the card should render — machinery lines stay stored but hidden. */
export function visibleKeyPoints(section: PlanSectionDraft): PlanKeyPointDraft[] {
  return section.keyPoints.filter((point) => !point.isMachinery)
}

/**
 * A LOSSLESS fingerprint of everything the card can edit, for answering "did
 * the writer change anything since this draft was sent?".
 *
 * Deliberately not `JSON.stringify(planDraftToLivePlan(draft))`: that serialiser
 * is lossy by design — it trims and DROPS empty key points, so a bullet the
 * writer added but hasn't typed into yet compares equal to no bullet at all.
 * Answering "nothing changed" there discards the draft, the empty bullet and
 * the writer's caret with it.
 *
 * Key points are keyed by id as well as text so a rename, a reorder and a
 * delete-plus-re-add are all distinguishable.
 *
 * **MUST cover every editable field on `PlanSectionDraft`.** A field the card
 * can change but the signature can't see compares equal, which reads as "the
 * writer changed nothing" and discards their work. If you add an editor for a
 * new field, add it here in the same commit.
 */
export function planDraftSignature(draft: PlanEditDraft): string {
  return JSON.stringify({
    goal: draft.goal,
    sections: draft.sections.map((section) => ({
      sectionId: section.sectionId,
      heading: section.heading,
      targetWordCount: section.targetWordCount,
      keyPoints: section.keyPoints.map((point) => [point.id, point.text]),
      // No editor today, but it is on the draft and in the save payload, so it
      // is one `evidenceHints` editor away from being a silent loss.
      evidenceHints: section.evidenceHints,
    })),
  })
}

/**
 * True when the draft holds something `planDraftToLivePlan` cannot express, so
 * a round trip cannot possibly bring it back.
 *
 * Today that is exactly one thing: a key point the writer has added but not yet
 * typed into. The serialiser drops empty bullets (rightly — an empty bullet in
 * the drafter's prompt is noise), which means saving persists a plan WITHOUT
 * the row the writer is looking at, with their caret in it. Dropping the draft
 * on that response deletes the row out from under them.
 *
 * Callers keep such a draft alive across a save; it is NOT by itself a reason
 * to call the draft dirty, or Save could never come clean.
 */
export function planDraftHasUnsavableContent(draft: PlanEditDraft): boolean {
  return draft.sections.some((section) =>
    section.keyPoints.some((point) => point.text.trim().length === 0)
  )
}

// ===================
// Serialisation
// ===================

/**
 * Rebuild the live plan from a draft by SPREADING the base — the ~9 rich fields
 * the card never sees ride through untouched. The server merges defensively too,
 * but local state is set from this object, so a literal here would strip them
 * from the card until the next reload.
 *
 * `coverageMap` is pruned of references to removed sections so the card stays
 * coherent between save and response; the server ignores the client's
 * `coverageMap` entirely and prunes its own.
 */
export function planDraftToLivePlan(draft: PlanEditDraft): LivePlanJson {
  // Sections carry rich fields too (`sectionType`, `draftingDirectives` —
  // backend/src/types/plan-patch-types.ts), so the same spread rule applies one
  // level down. Matched by `sectionId`, never by position: the draft can have
  // reordered or removed sections since it was forked.
  const storedSectionById = new Map(
    (draft.base.sections ?? []).map((section) => [section.sectionId, section])
  )
  const sections: LivePlanSection[] = draft.sections.map((section) => ({
    ...storedSectionById.get(section.sectionId),
    sectionId: section.sectionId,
    heading: section.heading,
    targetWordCount: section.targetWordCount,
    keyPoints: section.keyPoints
      // A writer who clicks "add point" and clicks away must not persist an
      // empty bullet into the drafter's prompt.
      .map((point) => point.text.trim())
      .filter((text) => text.length > 0),
    ...(section.evidenceHints ? { evidenceHints: section.evidenceHints } : {}),
  }))

  const liveSectionIds = new Set(sections.map((section) => section.sectionId))
  const coverageMap: Record<string, string[]> = {}
  for (const [requirementId, sectionIds] of Object.entries(draft.base.coverageMap ?? {})) {
    const kept = (sectionIds ?? []).filter((sectionId) => liveSectionIds.has(sectionId))
    if (kept.length > 0) coverageMap[requirementId] = kept
  }

  return { ...draft.base, goal: draft.goal, sections, coverageMap }
}

// ===================
// Client-side invariant pre-check
// ===================

/**
 * Mirror of `normalizeHeading` in `backend/src/lib/orchestrator/drafting-tools.ts`.
 * `proofPoints[].inHeading` / `soWhat[].inHeading` are string foreign keys into
 * `sections[].heading`, matched on the normalised form — so two sections that
 * normalise to the same heading make every reference to it ambiguous.
 */
export function normalizePlanHeading(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * Mirror of the server's request bounds (`UpdateLivePlanSectionSchema` /
 * `UpdateLivePlanContentSchema` in `backend/src/lib/schemas.ts`). A zod
 * rejection names no section and reads like a bug report, so the card checks
 * these first and points at the offending row. Keep in sync with the twin.
 */
export const PLAN_LIMITS = {
  goalChars: 4000,
  sections: 50,
  headingChars: 300,
  targetWordCount: 100000,
  keyPointsPerSection: 50,
  keyPointChars: 2000,
} as const

export interface PlanDraftValidationError {
  message: string
  conflictingSectionIds: string[]
}

/**
 * The invariants the server rejects with a 400 that the card can check first.
 * The server remains the authority — this only spares the writer a round trip
 * and gives them copy they can act on.
 */
export function validatePlanDraft(draft: PlanEditDraft): PlanDraftValidationError | null {
  if (draft.goal.trim().length === 0) {
    return { message: 'Give the plan a goal before saving.', conflictingSectionIds: [] }
  }
  if (draft.goal.length > PLAN_LIMITS.goalChars) {
    return {
      message: `The goal is too long — keep it under ${PLAN_LIMITS.goalChars} characters.`,
      conflictingSectionIds: [],
    }
  }
  if (draft.sections.length === 0) {
    return { message: 'A plan needs at least one section.', conflictingSectionIds: [] }
  }
  if (draft.sections.length > PLAN_LIMITS.sections) {
    return {
      message: `A plan can have at most ${PLAN_LIMITS.sections} sections.`,
      conflictingSectionIds: [],
    }
  }

  const seen = new Map<string, string>()
  for (const section of draft.sections) {
    const normalized = normalizePlanHeading(section.heading ?? '')
    if (normalized.length === 0) {
      return {
        message: 'Give every section a heading before saving.',
        conflictingSectionIds: [section.sectionId],
      }
    }
    const existing = seen.get(normalized)
    if (existing !== undefined) {
      return {
        message: `Two sections share the heading "${section.heading}" — give each one a distinct heading.`,
        conflictingSectionIds: [existing, section.sectionId],
      }
    }
    seen.set(normalized, section.sectionId)
  }

  for (const section of draft.sections) {
    if (section.heading.length > PLAN_LIMITS.headingChars) {
      return {
        message: `A section heading is too long — keep it under ${PLAN_LIMITS.headingChars} characters.`,
        conflictingSectionIds: [section.sectionId],
      }
    }
    if (!Number.isInteger(section.targetWordCount) || section.targetWordCount <= 0) {
      return {
        message: `"${section.heading}" needs a whole target word count above zero.`,
        conflictingSectionIds: [section.sectionId],
      }
    }
    if (section.targetWordCount > PLAN_LIMITS.targetWordCount) {
      return {
        message: `"${section.heading}" has an unrealistic target word count — keep it under ${PLAN_LIMITS.targetWordCount}.`,
        conflictingSectionIds: [section.sectionId],
      }
    }
    // Counted after the empty-point prune that `planDraftToLivePlan` applies, so
    // the check matches what is actually sent.
    const sentKeyPoints = section.keyPoints.filter((point) => point.text.trim().length > 0)
    if (sentKeyPoints.length > PLAN_LIMITS.keyPointsPerSection) {
      return {
        message: `"${section.heading}" has too many key points — keep it to ${PLAN_LIMITS.keyPointsPerSection}.`,
        conflictingSectionIds: [section.sectionId],
      }
    }
    if (sentKeyPoints.some((point) => point.text.trim().length > PLAN_LIMITS.keyPointChars)) {
      return {
        message: `A key point in "${section.heading}" is too long — keep each under ${PLAN_LIMITS.keyPointChars} characters.`,
        conflictingSectionIds: [section.sectionId],
      }
    }
  }

  return null
}
